import { useEffect, useMemo, useRef, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useQuery } from '../../contexts/RouterContext';
import { useAddToast } from '../../contexts/ToastContext';
import { useProjectsByLocators } from '../../hooks/api/projects/useProjectsByLocators';
import { getReleaseGroupTitleQueryKey, useReleaseGroupsByIds } from '../../hooks/api/releaseGroups/useReleaseGroupsByIds';
import queryClient from '../../ReactQuery/client';
import { get } from '../../redux/api';
import FormLabel from '../generic/Forms/FormLabel';
import LabeledSelect from '../generic/Forms/LabeledSelect';
import AsyncPaginatedSelect from '../generic/Select/AsyncPaginatedSelect';
import type { ProjectsResponse, ReleaseGroupsResponse } from '../../../shared/projectsV2/response';

import styles from './LegalAgentScopeSelect.module.less';
import { parseLegalAgentQuery, serializeLegalAgentQuery, withScopeKind } from './legalAgentQuery';
import type { LegalAgentQuery, LegalAgentScopeKind } from './legalAgentQuery';

export type ScopeOption = {
  label: string;
  value: string;
};

const PAGE_SIZE = 20;
const FIRST_PAGE = { page: 1 };

type OptionsPage = {
  options: ScopeOption[];
  hasMore: boolean;
  additional: { page: number };
};

type LoadOptions = (search: string, loaded: unknown, additional: { page: number } | undefined) => Promise<OptionsPage>;

//  A failed page toasts and then rejects. react-select-async-paginate caches a resolved page as final for that
//  search, so resolving an empty page would never be retried; a rejection keeps the paging state and retries on the
//  next open or scroll.
function withErrorToast(load: LoadOptions, onError: () => void): LoadOptions {
  return async (search, loaded, additional) => {
    try {
      return await load(search, loaded, additional);
    } catch (error) {
      onError();
      throw error;
    }
  };
}

export async function loadProjectOptions(
  search: string,
  _loaded: unknown,
  additional: { page: number } | undefined,
): Promise<OptionsPage> {
  const page = additional?.page ?? FIRST_PAGE.page;
  const params = { count: PAGE_SIZE, page, sort: 'title_asc', title: search };
  const response = await queryClient.ensureQueryData({
    queryKey: ['projects', 'legal-agent-scope-options', params],
    queryFn: () => get<ProjectsResponse>('/api/v2/projects', false, params, { encodeQuery: true }),
  });
  const projects = response.data?.projects ?? [];
  return {
    options: projects.map((project): ScopeOption => ({ label: project.title, value: project.id })),
    hasMore: page * PAGE_SIZE < (response.data?.total ?? 0),
    additional: { page: page + 1 },
  };
}

export async function loadReleaseGroupOptions(
  search: string,
  _loaded: unknown,
  additional: { page: number } | undefined,
): Promise<OptionsPage> {
  const page = additional?.page ?? FIRST_PAGE.page;
  const params = { count: PAGE_SIZE, page, sort: 'title_asc', title: search };
  const response = await queryClient.ensureQueryData({
    queryKey: ['release-groups', 'legal-agent-scope-options', params],
    queryFn: () => get<ReleaseGroupsResponse>('/api/v2/release-groups', false, params, { encodeQuery: true }),
  });
  const releaseGroups = response.data?.releaseGroups ?? [];
  return {
    options: releaseGroups.map((group): ScopeOption => ({ label: group.title, value: String(group.id) })),
    hasMore: page * PAGE_SIZE < (response.data?.total ?? 0),
    additional: { page: page + 1 },
  };
}

type KindOption = { label: string; value: LegalAgentScopeKind };

const KIND_OPTIONS: KindOption[] = [
  { label: 'Projects', value: 'projects' },
  { label: 'Release Groups', value: 'releaseGroups' },
];

/**
 * The Legal Agent portal's scope: the projects or release groups whose activity to show. Empty means every
 * viewable one of that kind.
 */
export const LegalAgentScopeSelect: VoidFunctionComponent = () => {
  const [query, setQuery] = useQuery<LegalAgentQuery, Record<string, unknown>>(parseLegalAgentQuery);
  const addToast = useAddToast();
  const isReleaseGroups = query.scope === 'releaseGroups';
  const releaseGroups = useReleaseGroupsByIds(isReleaseGroups ? query.releaseGroups : []);

  //  Options picked in the menu carry their titles. The lookup only labels locators restored from the URL.
  const [pickedLabels, setPickedLabels] = useState<Map<string, string>>(new Map());
  const unlabeled = useMemo(
    () => (isReleaseGroups ? [] : query.projects.filter((locator) => !pickedLabels.has(locator))),
    [isReleaseGroups, query.projects, pickedLabels],
  );
  const { data, isError } = useProjectsByLocators(unlabeled);
  const projectTitles = new Map((data?.projects ?? []).map((project) => [project.id, project.title]));

  //  The effect reruns when the query or callbacks change; each lookup result is acted on once.
  const handledLookup = useRef<unknown>(null);
  useEffect(() => {
    if (!data || handledLookup.current === data) {
      return;
    }
    handledLookup.current = data;
    const found = new Set(data.projects.map((project) => project.id));
    const missing = unlabeled.filter((locator) => !found.has(locator));
    if (missing.length === 0) {
      return;
    }
    setQuery(serializeLegalAgentQuery({
      ...query,
      projects: query.projects.filter((locator) => !missing.includes(locator)),
    }));
    addToast({
      type: 'error',
      content: missing.length === 1
        ? '1 selected project isn\'t available and was removed.'
        : `${missing.length} selected projects aren't available and were removed.`,
    });
  }, [data, unlabeled, query, setQuery, addToast]);

  const handledUnavailable = useRef('');
  const unavailableKey = releaseGroups.settled ? releaseGroups.unavailable.join(',') : '';
  useEffect(() => {
    if (unavailableKey === '') {
      handledUnavailable.current = '';
      return;
    }
    if (handledUnavailable.current === unavailableKey) {
      return;
    }
    handledUnavailable.current = unavailableKey;
    const missing = unavailableKey.split(',');
    setQuery(serializeLegalAgentQuery({
      ...query,
      releaseGroups: query.releaseGroups.filter((id) => !missing.includes(id)),
    }));
    addToast({
      type: 'error',
      content: missing.length === 1
        ? '1 selected release group isn\'t available and was removed.'
        : `${missing.length} selected release groups aren't available and were removed.`,
    });
  }, [unavailableKey, query, setQuery, addToast]);

  const lookupFailed = isReleaseGroups ? releaseGroups.hasError : isError;
  useEffect(() => {
    if (lookupFailed) {
      addToast({
        type: 'error',
        content: `Selected ${isReleaseGroups ? 'release groups' : 'projects'} couldn't be loaded. Try again in a moment.`,
      });
    }
  }, [lookupFailed, isReleaseGroups, addToast]);

  const titles = isReleaseGroups ? releaseGroups.titles : new Map([...projectTitles, ...pickedLabels]);
  const selected = isReleaseGroups ? query.releaseGroups : query.projects;
  const value = selected.map((id): ScopeOption => ({ label: titles.get(id) ?? id, value: id }));
  const loadOptions = withErrorToast(
    isReleaseGroups ? loadReleaseGroupOptions : loadProjectOptions,
    () => addToast({
      type: 'error',
      content: `${isReleaseGroups ? 'Release groups' : 'Projects'} couldn't be loaded. Try again in a moment.`,
    }),
  );

  return (
    <div className={styles.scopeSelect} data-test-id="legal-agent-scope-select">
      <LabeledSelect<KindOption>
        id="legal-agent-scope-kind"
        label="Work with"
        options={KIND_OPTIONS}
        value={KIND_OPTIONS.find((option) => option.value === query.scope) ?? KIND_OPTIONS[0]}
        isSearchable={false}
        onChange={(option) => {
          if (option && option.value !== query.scope) {
            setQuery(serializeLegalAgentQuery(withScopeKind(query, option.value)));
          }
        }}
      />
      <FormLabel label={isReleaseGroups ? 'Release groups' : 'Projects'} htmlFor="legal-agent-scope-ids">
        <AsyncPaginatedSelect<ScopeOption, { options: ScopeOption[] }, { page: number }, true>
          //  A new key per kind resets react-select-async-paginate's cached options.
          key={query.scope}
          isMulti
          inputId="legal-agent-scope-ids"
          placeholder={isReleaseGroups ? 'All release groups' : 'All projects'}
          value={value}
          loadOptions={loadOptions}
          additional={FIRST_PAGE}
          debounceTimeout={200}
          onChange={(options) => {
            //  The list already gave us each newly picked group's title; seed it so the chip does not refetch the group.
            //  Existing chips are skipped: their label may be the raw id fallback, which is not a title.
            if (isReleaseGroups) {
              options
                .filter((option) => !selected.includes(option.value))
                .forEach((option) => queryClient.setQueryData(getReleaseGroupTitleQueryKey(option.value), option.label));
            }
            const ids = options.map((option) => option.value);
            if (isReleaseGroups) {
              setQuery(serializeLegalAgentQuery({ ...query, releaseGroups: ids }));
              return;
            }
            //  A chip still showing its raw locator has no title to keep; it stays in the lookup.
            setPickedLabels(new Map(options.filter((option) => option.label !== option.value).map((option) => [option.value, option.label])));
            setQuery(serializeLegalAgentQuery({ ...query, projects: ids }));
          }}
        />
      </FormLabel>
    </div>
  );
};
